/* eslint-disable unicode-bom */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { ScreenGuard } from '../components/ScreenGuard';
import { SYMBOLS, formatPrice, applyLocalDiscount } from '../utils/priceHelpers';
import ListSelector from '../components/ListSelector';
import ManageListsModal from '../components/ManageListsModal';
import { addUserBuyListItemCalc } from '../services/buyLists';

// Full currency list used in Settings
const CURRENCY_META_FULL = [
  ['EUR', '€', 'eu'], ['USD', '$', 'us'], ['GBP', '£', 'gb'], ['CAD', '$', 'ca'],
  ['AUD', '$', 'au'], ['CNY', '¥', 'cn'], ['KRW', '₩', 'kr'], ['JPY', '¥', 'jp'],
  ['RUB', '₽', 'ru'], ['SEK', 'KR', 'se'], ['DKK', 'KR', 'dk'], ['NOK', 'KR', 'no'],
  ['CHF', 'Fr.', 'ch'], ['PLN', 'zł', 'pl'], ['CZK', 'Kč', 'cz'],
  ['HRK', 'kn', 'hr'], ['RON', 'lei', 'ro'], ['TRY', '₺', 'tr'], ['INR', '₹', 'in'],
  ['ZAR', 'R', 'za'], ['BRL', 'R$', 'br'], ['MXN', '$', 'mx'], ['MYR', 'RM', 'my'],
  ['AED', 'د.إ', 'ae'], ['SAR', 'SR', 'sa'], ['KWD', 'KD', 'kw'], ['JOD', 'د.أ', 'jo'],
  ['EGP', 'E£', 'eg'], ['MAD', 'DH', 'ma'], ['DZD', 'دج', 'dz'], ['TND', 'DT', 'tn'],
  ['ETB', 'Br', 'et'], ['UZS', "so'm", 'uz'],
];

function getNarrowSymbol(code) {
  try {
    const parts = new Intl.NumberFormat('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).formatToParts(0);
    const sym = parts.find(p => p.type === 'currency')?.value;
    return sym || code;
  } catch (_) {
    return code;
  }
}

function normalizeCurrencyCode(value) {
  const code = String(value || 'USD').trim().toUpperCase();
  return code === 'BGN' ? 'EUR' : code || 'USD';
}

function CurrencyPicker({ value, onChange, isForest, theme }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const listRef = React.useRef(null);
  React.useEffect(() => {
    function onDocClick(e) {
      if (!btnRef.current) return;
      if (btnRef.current.contains(e.target) || (listRef.current && listRef.current.contains(e.target))) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const current = React.useMemo(() => CURRENCY_META_FULL.find(([c]) => c === value) || CURRENCY_META_FULL[1], [value]);

  const btnStyle = {
    backgroundColor: isForest ? 'rgba(6,78,59,0.5)' : theme?.surface?.card || '#ffffff',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme?.surface?.cardBorder || '#e5e7eb'}`,
    color: isForest ? '#e8f5e9' : theme?.text?.primary || '#1f2937',
  };

  const listStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.98)' : theme?.surface?.card || '#ffffff',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme?.surface?.cardBorder || '#e5e7eb'}`,
    boxShadow: isForest ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.15)',
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="min-w-[60px] max-w-full truncate rounded-xl text-sm px-3 h-10 inline-flex items-center gap-2 focus:outline-none"
        style={btnStyle}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={current[0]}
        onKeyDown={(e) => { if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } if (open && e.key === "Escape") { e.preventDefault(); setOpen(false); btnRef.current?.focus(); } }}
        ref={btnRef}
      >
        <span className={`fi fi-${current[2]} rounded-sm`} />
        <span className="font-medium">{getNarrowSymbol(current[0])} {current[0]}</span>
      </button>
      {open && (
        <ul ref={listRef} className="absolute right-0 z-20 mt-1 max-h-64 w-max min-w-full overflow-auto rounded-xl" style={listStyle} role="listbox">
          {CURRENCY_META_FULL.map(([code, sym, iso]) => (
            <li
              key={code}
              role="option"
              aria-selected={code === value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(code); setOpen(false); }}
              className="px-3 py-2 flex items-center gap-2 cursor-pointer whitespace-nowrap"
              style={{
                backgroundColor: code === value ? (isForest ? 'rgba(6,78,59,0.7)' : 'rgba(76,175,80,0.1)') : 'transparent',
                color: isForest ? '#e8f5e9' : theme?.text?.primary || '#1f2937',
              }}
              onMouseEnter={(e) => {
                if (code !== value) e.currentTarget.style.backgroundColor = isForest ? 'rgba(6,78,59,0.4)' : 'rgba(0,0,0,0.03)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = code === value ? (isForest ? 'rgba(6,78,59,0.7)' : 'rgba(76,175,80,0.1)') : 'transparent';
              }}
            >
              <span className={`fi fi-${iso} rounded-sm`} />
              <span>{getNarrowSymbol(code)} {code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Calculator() {
  const { ready, uid, settings, gates, pricing, claims } = useUser();
  const { theme: t, isForest } = useTheme();
  const { t: tr } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editItem = location.state?.editItem;
  const isAdmin = !!claims?.admin;
  const canUse = !!gates?.navigation?.calculator;
  const [unit, setUnit] = React.useState('KG');
  const [currency, setCurrency] = React.useState('USD');

  // Header discount (custom) like BuyingLists
  const [discountPct, setDiscountPct] = React.useState(0);
  React.useEffect(() => {
    const v = Number(settings?.buyListDiscount) || 0;
    setDiscountPct(v);
  }, [settings?.buyListDiscount]);

  const [pt, setPt] = React.useState(0);
  const [pd, setPd] = React.useState(0);
  const [rh, setRh] = React.useState(0);
  const [weight, setWeight] = React.useState(0);
  const [moisture, setMoisture] = React.useState(0.9);
  const [expenses, setExpenses] = React.useState(0);

  const [marketDefaults, setMarketDefaults] = React.useState({ pt: 0, pd: 0, rh: 0 });
  const [market, setMarket] = React.useState({ pt: 0, pd: 0, rh: 0 });
  const [chargesDoc, setChargesDoc] = React.useState(null);
  const [dailyPrices, setDailyPrices] = React.useState({});
  const [manageOpen, setManageOpen] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [added, setAdded] = React.useState(false);

  // Validation error states
  const [weightError, setWeightError] = React.useState('');
  const [metalsError, setMetalsError] = React.useState('');

  // Hydrate from editItem
  React.useEffect(() => {
    if (editItem) {
      setPt(Number(editItem.pt) || 0);
      setPd(Number(editItem.pd) || 0);
      setRh(Number(editItem.rh) || 0);

      const g = Number(editItem.weightCeramic) || 0;
      setWeight(unit === 'KG' ? g / 1000 : g);

      setMoisture(Number(editItem.moisture) || 0);
      setExpenses(Number(editItem.shippingCost) || 0);
    }
  }, [editItem, unit]);

  React.useEffect(() => {
    if (settings?.currency) setCurrency(normalizeCurrencyCode(settings.currency));
  }, [settings?.currency]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const dSnap = await getDoc(doc(db, 'prices', 'DailyPrices'));
        const cSnap = await getDoc(doc(db, 'prices', 'Charges'));
        if (!alive) return;
        const d = dSnap.exists() ? dSnap.data() : {};
        const def = { pt: Number(d.platUSD) || 0, pd: Number(d.pallUSD) || 0, rh: Number(d.rhodUSD) || 0 };
        setMarketDefaults(def);
        setMarket(def);
        setDailyPrices(d || {});
        setChargesDoc(cSnap.exists() ? cSnap.data() : null);
      } catch (e) {
      }
    })();
    return () => { alive = false; };
  }, []);

  function clearInputs() {
    setPt(0); setPd(0); setRh(0); setWeight(0); setMoisture(0.9); setExpenses(0);
  }
  function resetMarket() { setMarket(marketDefaults); }

  const sym = SYMBOLS[currency] || '';

  // Math (derived)
  const derived = React.useMemo(() => {
    const charges = chargesDoc || {};
    const selWeight = unit;
    const ozGrams = 31.1034768;
    const grams = selWeight === 'KG' ? (Number(weight) || 0) * 1000 : (Number(weight) || 0);

    const effWeight = grams * ((100 - (Number(moisture) || 0)) / 100);

    const plat = (Number(pt) || 0) * (Number(charges.metalDeductionPlat) || 0);
    const pall = (Number(pd) || 0) * (Number(charges.metalDeductionPall) || 0);
    const rhod = (Number(rh) || 0) * (Number(charges.metalDeductionRhod) || 0);

    /*
     * NAIM CALCULATOR MARKET PRICES RULES - DO NOT MODIFY!
     * - ONLY admin sees this section (hidden for non-admin)
     * - Admin can EDIT market prices, which affects the calculation
     * - Calculations use `market` state (initialized from DailyPrices)
     */
    let marketPlat = (Number(market.pt) || 0) - (Number(charges.ounceDeductionPlat) || 0);
    let marketPall = (Number(market.pd) || 0) - (Number(charges.ounceDeductionPall) || 0);
    let marketRhod = (Number(market.rh) || 0) - (Number(charges.ounceDeductionRhod) || 0);

    // Treatment charge uses raw weight (not moisture-adjusted) to match cloud function
    const priceReduction = grams * ((Number(charges.treatmentCharge) || 0) / 1000);

    const refiningCostPlat = (plat / 1000) * (Number(charges.refiningCostPlat) || 0);
    const refiningCostPall = (pall / 1000) * (Number(charges.refiningCostPall) || 0);
    const refiningCostRhod = (rhod / 1000) * (Number(charges.refiningCostRhod) || 0);

    // per-gram from per-oz
    marketPlat = marketPlat / ozGrams;
    marketPall = marketPall / ozGrams;
    marketRhod = marketRhod / ozGrams;

    const pricePlat = (plat * marketPlat * effWeight) / 1_000_000;
    const pricePall = (pall * marketPall * effWeight) / 1_000_000;
    const priceRhod = (rhod * marketRhod * effWeight) / 1_000_000;

    let totalPrice = pricePlat + pricePall + priceRhod
      - refiningCostPlat - refiningCostPall - refiningCostRhod
      - priceReduction;

    // Leasing
    const leasingPlat = ((pricePlat * (Number(charges.leasingPlat) || 0)) / 360) * (Number(charges.leasingPlatDays) || 0);
    const leasingPall = ((pricePall * (Number(charges.leasingPall) || 0)) / 360) * (Number(charges.leasingPallDays) || 0);
    const leasingRhod = ((priceRhod * (Number(charges.leasingRhod) || 0)) / 360) * (Number(charges.leasingRhodDays) || 0);
    totalPrice = totalPrice - leasingPlat - leasingPall - leasingRhod;

    // Shipping (expenses) per kg
    const shipping = (Number(expenses) || 0) * (effWeight / 1000);
    totalPrice = totalPrice - shipping;

    if (totalPrice < 0) totalPrice = 0;

    // currency conversion from DailyPrices (e.g., eurUSD)
    const curKey = String(currency || 'USD').slice(-3).toLowerCase() + 'USD';
    const rate = Number(dailyPrices?.[curKey]) || 1;

    // Base local (no role/personal discount); stored in Buy Lists
    const baseLocal = Math.round(totalPrice * rate * 100) / 100;

    // Apply role/personal discount the same way Converters/Favorites do
    const discountedLocal = applyLocalDiscount(baseLocal, totalPrice, pricing);

    // Extra on-page manual discount (header control)
    const afterCustom = (typeof discountedLocal === 'number' ? discountedLocal : baseLocal) * (1 - (Number(discountPct) || 0) / 100);

    const final = Math.round(afterCustom * 100) / 100;

    const pricePerKg = effWeight > 0 ? Math.round(((final) / (effWeight / 1000)) * 100) / 100 : 0;

    return { total: final, pricePerKg, baseLocal, rate };
  }, [chargesDoc, unit, weight, moisture, pt, pd, rh, market, expenses, discountPct, currency, dailyPrices, pricing]);

  // Add current calculation to selected Buy List
  const handleAddToList = React.useCallback(async () => {
    if (!uid) return;

    // Clear previous errors
    setWeightError('');
    setMetalsError('');

    // Validation: weight is required
    const weightNum = Number(weight) || 0;
    if (weightNum <= 0) {
      setWeightError(tr('calculator.weightRequired'));
      return;
    }

    // Validation: at least one metal value is required
    const ptNum = Number(pt) || 0;
    const pdNum = Number(pd) || 0;
    const rhNum = Number(rh) || 0;
    if (ptNum <= 0 && pdNum <= 0 && rhNum <= 0) {
      setMetalsError(tr('calculator.metalsRequired'));
      return;
    }

    const curKey = String(currency || 'USD').slice(-3).toLowerCase() + 'USD';
    const rate = Number(dailyPrices?.[curKey]) || 1;
    // Use base (un-discounted) local to derive USD; BuyingLists applies discounts uniformly
    const baseLocal = typeof derived.baseLocal === 'number' ? derived.baseLocal : (derived.total || 0);
    const priceUSD = rate > 0 ? baseLocal / rate : 0;
    if (!priceUSD || !isFinite(priceUSD)) return;

    const currencies = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'AED', 'RUB', 'CNY', 'KRW', 'INR', 'ZAR', 'BRL', 'MXN', 'MYR', 'SEK', 'DKK', 'NOK', 'CHF', 'PLN', 'CZK', 'HRK', 'TRY', 'RON', 'SAR', 'KWD', 'JOD', 'EGP', 'MAD', 'DZD', 'TND', 'ETB', 'UZS'];
    const prices = Object.fromEntries(currencies.map((c) => {
      const key = c.toLowerCase() + 'USD';
      const r = Number(dailyPrices?.[key]) || 1;
      const v = Math.round(priceUSD * r * 100) / 100;
      return ['price' + c, v];
    }));

    const grams = unit === 'KG' ? (Number(weight) || 0) * 1000 : (Number(weight) || 0);
    const item = {
      material: 'CALCULATOR',
      code: 'CALCULATOR',
      brand: 'CALCULATOR',
      // PPM as strings
      pt: String(Number(pt) || 0),
      pd: String(Number(pd) || 0),
      rh: String(Number(rh) || 0),
      // Persist grams for ceramic; weight stays 0 per app behavior
      weight: 0,
      weightCeramic: grams,
      // Moisture as string (e.g., "0.9")
      moisture: String(Number(moisture) || 0),
      // shippingCost: '' when zero; otherwise a number
      shippingCost: (Number(expenses) || 0) === 0 ? '' : Number(expenses),
      imgURL: 'https://firebasestorage.googleapis.com/v0/b/ecat-5324c.appspot.com/o/converters%2Fcalculator.png?alt=media&token=f865d14f-9708-456e-a29a-68482cb7b6bd',
      // Store UN-DISCOUNTED prices map (USD base -> all currencies)
      prices,
    };

    try {
      if (editItem && editItem.id) {
        const currentList = settings?.selectedList || 'default';
        const docRef = doc(db, 'buyLists', uid, currentList, editItem.id);
        await updateDoc(docRef, {
          ...item,
          total: Number(editItem.total) || 1
        });
        navigate('/buying-lists');
      } else {
        await addUserBuyListItemCalc({ uid, item, total: 1 });
      }
    } catch (_) {
      // no-op
    }
  }, [uid, currency, dailyPrices, derived.total, derived.baseLocal, unit, weight, pt, pd, rh, moisture, expenses, tr, editItem, settings?.selectedList, navigate]);

  // Theme-based styles
  const buttonStyle = {
    backgroundColor: isForest ? t.surface.card : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
    color: isForest ? t.text.primary : t.text.primary,
  };

  const wrapperStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.6)' : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
  };

  const selectStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
    color: isForest ? '#e8f5e9' : t.text.primary,
  };

  const sectionStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : t.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`,
    color: isForest ? '#e8f5e9' : t.text.primary,
  };

  const inputFieldStyle = {
    backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.input.border}`,
    color: isForest ? '#e8f5e9' : t.text.primary,
  };

  const labelStyle = { color: isForest ? '#a5d6a7' : t.text.secondary };

  return (
    <ScreenGuard>
      <div className="px-0 md:px-0 pt-[2px] overflow-x-hidden" style={{ color: isForest ? t.text.primary : t.text.primary }}>
        {!ready ? (
          <div className="p-6" style={{ color: isForest ? t.text.tertiary : t.text.tertiary }}>{tr('calculator.loading')}</div>
        ) : !uid ? (
          <div className="p-6" style={{ color: t.status.error }}>{tr('calculator.signInRequired')}</div>
        ) : !canUse ? (
          <div className="p-6" style={{ color: isForest ? t.text.secondary : t.text.secondary }}>{tr('calculator.noAccess')}</div>
        ) : (
          <>
            {/* Controls row: Discount left; Unit/Currency + Lists right */}

            {/* Mobile controls */}
            <div className="md:hidden">
              {/* Row 1: Lists */}
              <div className="mb-2 flex items-center justify-end gap-2">
                <ListSelector />
                <button
                  onClick={() => setManageOpen(true)}
                  className="px-2 py-1 rounded-lg transition-colors"
                  style={buttonStyle}
                  title="Manage lists"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              {/* Row 2: Discount left, Unit/Currency right */}
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex rounded-md px-3 py-2 h-10 items-center justify-center" style={wrapperStyle}>
                  <span style={{ color: isForest ? '#a5d6a7' : t.text.secondary }} className="whitespace-nowrap mr-2">{tr('calculator.discount')}</span>
                  <div className="inline-flex items-stretch h-8 rounded-md overflow-hidden" style={{ border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }}>
                    <button className="px-2 text-sm" style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, color: isForest ? '#e8f5e9' : t.text.primary, borderRight: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }} onClick={() => setDiscountPct((v) => Math.max(-100, Math.min(100, (Number(v) || 0) - 5)))} title="Decrease discount by 5">-</button>
                    <input type="number" value={discountPct} onChange={(e) => { const n = parseInt(e.target.value, 10); if (!Number.isNaN(n)) setDiscountPct(Math.max(-100, Math.min(100, n))); }} onBlur={() => setDiscountPct((v) => Math.max(-100, Math.min(100, Number(v) || 0)))} step={5} min={-100} max={100} className="w-12 text-center px-0 focus:outline-none" style={{ backgroundColor: isForest ? 'rgba(15,58,48,0.6)' : t.input.background, color: discountPct < 0 ? '#ef4444' : discountPct > 0 ? '#3b82f6' : (isForest ? '#e8f5e9' : t.text.primary) }} />
                    <button className="px-2 text-sm" style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, color: isForest ? '#e8f5e9' : t.text.primary, borderLeft: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }} onClick={() => setDiscountPct((v) => Math.max(-100, Math.min(100, (Number(v) || 0) + 5)))} title="Increase discount by 5">+</button>
                  </div>
                </div>
                <div className="flex min-w-0 items-center gap-[5px] ml-2">
                  <select className="rounded-xl text-sm px-3 h-10 focus:outline-none" style={selectStyle} value={unit} onChange={(e) => setUnit(e.target.value)}>
                    <option value="KG">KG</option>
                    <option value="GM">GM</option>
                  </select>
                  <CurrencyPicker value={currency} onChange={setCurrency} isForest={isForest} theme={t} />
                </div>
              </div>
            </div>

            {/* Controls row: Discount left; Unit/Currency next to it; Lists to the far right */}
            <div className="hidden md:flex items-center justify-end gap-[10px] mt-0 mb-2">
              <div className="flex rounded-md px-3 py-2 h-10 items-center justify-center min-w-fit gap-2" style={wrapperStyle}>
                <span style={labelStyle} className="whitespace-nowrap">{tr('calculator.discount')}</span>
                <div className="inline-flex items-stretch h-8 rounded-md overflow-hidden" style={{ border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }}>
                  <button
                    className="px-2 text-sm"
                    style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, color: isForest ? '#e8f5e9' : t.text.primary, borderRight: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }}
                    onClick={() =>
                      setDiscountPct((v) =>
                        Math.max(-100, Math.min(100, (Number(v) || 0) - 5))
                      )
                    }
                    title="Decrease discount by 5"
                  >
                    -
                  </button>

                  <input
                    type="number"
                    value={discountPct}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n))
                        setDiscountPct(Math.max(-100, Math.min(100, n)));
                    }}
                    onBlur={() =>
                      setDiscountPct((v) =>
                        Math.max(-100, Math.min(100, Number(v) || 0))
                      )
                    }
                    step={5}
                    min={-100}
                    max={100}
                    className="w-12 text-center px-0 focus:outline-none"
                    style={{ backgroundColor: isForest ? 'rgba(15,58,48,0.6)' : t.input.background, color: discountPct < 0 ? '#ef4444' : discountPct > 0 ? '#3b82f6' : (isForest ? '#e8f5e9' : t.text.primary) }}
                  />

                  <button
                    className="px-2 text-sm"
                    style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, color: isForest ? '#e8f5e9' : t.text.primary, borderLeft: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}` }}
                    onClick={() =>
                      setDiscountPct((v) =>
                        Math.max(-100, Math.min(100, (Number(v) || 0) + 5))
                      )
                    }
                    title="Increase discount by 5"
                  >
                    +
                  </button>
                </div>
              </div>
              {/* Unit + Currency spaced 20px (ml-5) from Discount wrapper */}
              <div className="flex items-center gap-[10px]">
                <select className="rounded-xl text-sm px-3 h-10 focus:outline-none" style={selectStyle} value={unit} onChange={(e) => setUnit(e.target.value)}>
                  <option value="KG">KG</option>
                  <option value="GM">GM</option>
                </select>
                <CurrencyPicker value={currency} onChange={setCurrency} isForest={isForest} theme={t} />
              </div>
              {/* Lists on the far right */}
              <div className="ml-auto flex items-center gap-2">
                <ListSelector />
                <button
                  onClick={() => setManageOpen(true)}
                  className="px-2 py-1 rounded-lg"
                  style={buttonStyle}
                  title="Manage lists"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M11.983 1.574a1 1 0 0 0-1.966 0l-.196 1.178a6.987 6.987 0 0 0-1.36.787L6.39 2.887a1 1 0 0 0-1.414 1.414l.652 1.07a6.987 6.987 0 0 0-.787 1.36l-1.178.196a1 1 0 0 0 0 1.966l1.178.196c.175.48.43.925.756 1.318l-.652 1.07a1 1 0 1 0 1.414 1.414l1.07-.652c.393.326.838.58 1.318.756l1.07.652a1 1 0 1 0 1.414-1.414l-.652-1.07c.326-.393.58-.838.756-1.318l1.178-.196a1 1 0 0 0 0-1.966l-1.178-.196a6.987 6.987 0 0 0-.756-1.318l.652-1.07A1 1 0 0 0 13.61 2.887l-1.07.652a6.987 6.987 0 0 0-1.36-.787l-.196-1.178ZM10 7a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="mb-4 rounded-xl p-4" style={wrapperStyle}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4 min-w-0">
                  <div className="text-lg">
                    <span style={labelStyle}>{tr('calculator.totalValue')}:</span>{' '}
                    <span className="tabular-nums">{sym} {derived.total.toFixed(2)}</span>
                  </div>
                  <div className="text-sm">
                    <span style={labelStyle}>{tr('calculator.pricePerKg')}:</span>{' '}
                    <span className="tabular-nums">{formatPrice(derived.pricePerKg, currency)}</span>
                  </div>
                </div>
                <div className="hidden">
                  {/* Removed duplicate hidden controls */}
                  <select
                    className="rounded-md px-2 py-2 font-medium"
                    style={selectStyle}
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="KG">KG</option>
                    <option value="GM">GM</option>
                  </select>
                  <CurrencyPicker value={currency} onChange={setCurrency} isForest={isForest} theme={t} />
                </div>
              </div>
            </div>

            {/*
             * NAIM: Market Prices section - ADMIN ONLY, EDITABLE
             * Admin can edit these values which affects the price calculation.
             * DO NOT change visibility rules without understanding the difference from ECAT!
             */}
            {isAdmin && (
              <section className="rounded-xl p-4 md:p-5 shadow mb-6" style={sectionStyle}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-medium">{tr('calculator.marketPrices')}</h2>
                  <button className="px-3 py-2 rounded-md" style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`, color: isForest ? '#e8f5e9' : t.text.primary }} onClick={resetMarket}>{tr('calculator.reset')}</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* PD */}
                  <div>
                    <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.pd')}</label>
                    <input
                      type="text"
                      className="w-full rounded-md p-2"
                      style={inputFieldStyle}
                      value={Math.round(Number(market.pd) || 0)}
                      onChange={(e) => setMarket((m) => ({ ...m, pd: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  {/* PT */}
                  <div>
                    <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.pt')}</label>
                    <input
                      type="text"
                      className="w-full rounded-md p-2"
                      style={inputFieldStyle}
                      value={Math.round(Number(market.pt) || 0)}
                      onChange={(e) => setMarket((m) => ({ ...m, pt: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  {/* RH */}
                  <div>
                    <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.rh')}</label>
                    <input
                      type="text"
                      className="w-full rounded-md p-2"
                      style={inputFieldStyle}
                      value={Math.round(Number(market.rh) || 0)}
                      onChange={(e) => setMarket((m) => ({ ...m, rh: Number(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
              </section>
            )}

            <section className="rounded-xl p-4 md:p-5 shadow mb-6" style={sectionStyle}>
              <h2 className="text-lg font-medium mb-3">{tr('calculator.ppm')}</h2>
              {/* Row 1: PT, PD, RH */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.pt')}</label>
                  <input
                    type="number"
                    className="w-full rounded-md p-2"
                    style={inputFieldStyle}
                    value={pt}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setPt(''); }}
                    onBlur={() => { if (pt === '' || isNaN(Number(pt))) setPt(0); }}
                    onChange={(e) => { setPt(e.target.value === '' ? '' : Number(e.target.value) || 0); if (metalsError) setMetalsError(''); }}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.pd')}</label>
                  <input
                    type="number"
                    className="w-full rounded-md p-2"
                    style={inputFieldStyle}
                    value={pd}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setPd(''); }}
                    onBlur={() => { if (pd === '' || isNaN(Number(pd))) setPd(0); }}
                    onChange={(e) => { setPd(e.target.value === '' ? '' : Number(e.target.value) || 0); if (metalsError) setMetalsError(''); }}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.rh')}</label>
                  <input
                    type="number"
                    className="w-full rounded-md p-2"
                    style={inputFieldStyle}
                    value={rh}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setRh(''); }}
                    onBlur={() => { if (rh === '' || isNaN(Number(rh))) setRh(0); }}
                    onChange={(e) => { setRh(e.target.value === '' ? '' : Number(e.target.value) || 0); if (metalsError) setMetalsError(''); }}
                  />
                </div>
              </div>
              {/* Row 2: Weight, Moisture, Expenses */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.weight')}</label>
                  <input
                    type="number"
                    className="w-full rounded-md p-2"
                    style={inputFieldStyle}
                    value={weight}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setWeight(''); }}
                    onBlur={() => { if (weight === '' || isNaN(Number(weight))) setWeight(0); }}
                    onChange={(e) => { setWeight(e.target.value === '' ? '' : Number(e.target.value) || 0); if (weightError) setWeightError(''); }}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.moisture')}</label>
                  <input type="number" step="0.01" className="w-full rounded-md p-2" style={inputFieldStyle} value={moisture} onChange={(e) => setMoisture(Number(e.target.value) || 0)} />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={labelStyle}>{tr('calculator.expenses')}</label>
                  <input
                    type="number"
                    className="w-full rounded-md p-2"
                    style={inputFieldStyle}
                    value={expenses}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setExpenses(''); }}
                    onBlur={() => { if (expenses === '' || isNaN(Number(expenses))) setExpenses(0); }}
                    onChange={(e) => setExpenses(e.target.value === '' ? '' : Number(e.target.value) || 0)}
                  />
                </div>
              </div>

              {/* Validation Errors */}
              {weightError && <p className="text-red-500 text-sm mt-2">{weightError}</p>}
              {metalsError && <p className="text-red-500 text-sm mt-2">{metalsError}</p>}

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={async () => {
                    if (adding) return;
                    try {
                      setAdding(true);
                      await handleAddToList();
                      setAdded(true);
                      setTimeout(() => setAdded(false), 900);
                    } finally {
                      setAdding(false);
                    }
                  }}
                  disabled={adding}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium shadow"
                  style={{
                    backgroundColor: adding ? (isForest ? 'rgba(6,78,59,0.7)' : '#9ca3af') : added ? '#22c55e' : '#4caf50',
                    color: adding ? (isForest ? '#a5d6a7' : '#6b7280') : '#ffffff',
                    cursor: adding ? 'wait' : 'pointer',
                  }}
                  title="Add this calculation to your selected list"
                  aria-busy={adding || undefined}
                >
                  <span aria-hidden="true">
                    {added ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    )}
                  </span>
                  <span>{added ? (editItem ? tr('common.save') : tr('calculator.added')) : (adding ? (editItem ? tr('common.saving') : tr('calculator.adding')) : (editItem ? "Update" : tr('calculator.addToBuyList')))}</span>
                </button>
                <button className="px-3 py-2 rounded-md" style={{ backgroundColor: isForest ? 'rgba(6,78,59,0.7)' : t.surface.card, border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : t.surface.cardBorder}`, color: isForest ? '#e8f5e9' : t.text.primary }} onClick={clearInputs}>{tr('calculator.reset')}</button>
              </div>
            </section>

            <ManageListsModal open={manageOpen} onClose={() => setManageOpen(false)} />
          </>
        )}
      </div>
    </ScreenGuard>
  );
}



